import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingCart, Boxes, CreditCard, Banknote } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, bazaarOrdersService, platformFeeConfigService } from '../services/api';
import { useBazaarCartStore } from '../stores/bazaarCartStore';

type DeliveryChoice = 'collect' | 'post-job';

export function BazaarBasketPage() {
  const navigate = useNavigate();
  const { getToken, account } = useAppAuth();
  const { items, removeItem, updateQuantity, setItemOnlineAmount, grandTotal, totalDeposit, totalCod } = useBazaarCartStore();
  const [deliveryChoice, setDeliveryChoice] = useState<DeliveryChoice>('collect');
  const [itemErrors, setItemErrors] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);

  const { data: fees } = useQuery({
    queryKey: ['platform', 'fee-config'],
    queryFn: () => platformFeeConfigService(createApiClient(getToken)).getEffective(),
    enabled: !!account,
  });

  // PlatformFeeConfig.bazaarDepositPercent comes back as a 0–1 fraction (e.g. 0.25), not a 0–100
  // percent — convert once here so the rest of this page can work in whole percentage points.
  const minDepositPercent = (fees?.bazaarDepositPercent ?? 0.25) * 100;
  const requiresDelivery = deliveryChoice === 'post-job';
  const total   = grandTotal();
  // Cash on delivery only makes sense when the buyer and seller actually meet — a delivery
  // order (no in-person collection) is always charged in full online, overriding whatever
  // per-item online/COD split the buyer set while "I'll collect it myself" was selected.
  const deposit = requiresDelivery ? total : totalDeposit(minDepositPercent);
  const cod     = requiresDelivery ? 0 : totalCod(minDepositPercent);

  const itemOnlineAmount = (item: (typeof items)[number]) =>
    item.onlineAmount ?? Math.round(item.unitPrice * item.quantity * (minDepositPercent / 100) * 100) / 100;

  const validateItem = (item: (typeof items)[number], onlineAmount: number): string | null => {
    const lineTotal = item.unitPrice * item.quantity;
    const min = Math.round(lineTotal * (minDepositPercent / 100) * 100) / 100;
    if (onlineAmount < min - 0.01) return `Minimum online payment is £${min.toFixed(2)} (${minDepositPercent.toFixed(0)}%).`;
    if (onlineAmount > lineTotal + 0.01) return `Can't exceed the item total of £${lineTotal.toFixed(2)}.`;
    return null;
  };

  const handleOnlineAmountChange = (item: (typeof items)[number], raw: string) => {
    const value = parseFloat(raw);
    const online = Number.isFinite(value) ? value : 0;
    setItemOnlineAmount(item.listingId, online);
    setItemErrors(prev => ({ ...prev, [item.listingId]: validateItem(item, online) }));
  };

  const handleCodAmountChange = (item: (typeof items)[number], raw: string) => {
    const value = parseFloat(raw);
    const lineTotal = item.unitPrice * item.quantity;
    const codValue = Number.isFinite(value) ? value : 0;
    const online = Math.round((lineTotal - codValue) * 100) / 100;
    setItemOnlineAmount(item.listingId, online);
    setItemErrors(prev => ({ ...prev, [item.listingId]: validateItem(item, online) }));
  };

  const hasErrors = Object.values(itemErrors).some(Boolean);

  const placeOrderMutation = useMutation({
    mutationFn: () =>
      bazaarOrdersService(createApiClient(getToken)).create({
        items: items.map(i => ({
          listingId: i.listingId,
          quantity: i.quantity,
          quoteId: i.quoteId,
          // onlineAmount is ignored server-side for a delivery item (charged in full online
          // regardless), so there's no min/split to send once requiresDelivery is set.
          onlineAmount: requiresDelivery ? undefined : itemOnlineAmount(i),
          requiresDelivery,
        })),
      }),
    onSuccess: (result) => {
      navigate(`/bazaar/checkout/${result.orderId}`, {
        state: { clientSecret: result.clientSecret, depositAmount: result.depositAmount },
      });
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Could not start checkout. Please try again.'),
  });

  if (!account) return null;

  return (
    <div data-testid="bazaar-basket-screen" className="page-shell-tight">
      <Link to="/bazaar" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Bazaar
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <ShoppingCart className="w-6 h-6" /> Your Basket
      </h1>

      {items.length === 0 ? (
        <div data-testid="basket-empty" className="text-center py-16 text-gray-500 bg-white border border-gray-200 rounded-xl">
          Your basket is empty.
          <div className="mt-3">
            <Link to="/bazaar" className="text-sm font-semibold text-blue-600 hover:underline">Browse Bazaar</Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
              {items.map(item => {
                const lineTotal = item.unitPrice * item.quantity;
                const online = requiresDelivery ? lineTotal : itemOnlineAmount(item);
                const codForItem = requiresDelivery ? 0 : Math.round((lineTotal - online) * 100) / 100;
                const minForItem = Math.round(lineTotal * (minDepositPercent / 100) * 100) / 100;
                const itemError = itemErrors[item.listingId];
                return (
                  <div key={item.listingId} data-testid={`basket-line-${item.listingId}`} className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.listingTitle}</p>
                        <p className="text-xs text-gray-500">Sold by {item.sellerName}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            data-testid={`basket-decrement-${item.listingId}`}
                            onClick={() => updateQuantity(item.listingId, Math.max(1, item.quantity - 1))}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm w-6 text-center">{item.quantity}</span>
                          <button
                            data-testid={`basket-increment-${item.listingId}`}
                            onClick={() => updateQuantity(item.listingId, item.quantity + 1)}
                            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                          <button
                            data-testid={`basket-remove-${item.listingId}`}
                            onClick={() => removeItem(item.listingId)}
                            className="ml-2 text-xs text-red-600 hover:underline flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                      <div className="text-sm font-bold text-gray-900">£{lineTotal.toFixed(2)}</div>
                    </div>

                    <div className="flex gap-3 mt-3">
                      <div className="flex-1">
                        <label className="text-[10px] font-semibold text-gray-500 flex items-center gap-1 mb-1">
                          <CreditCard className="w-3 h-3" /> Pay online (min £{minForItem.toFixed(2)})
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <span className="px-2 text-xs text-gray-500">£</span>
                          <input
                            type="number"
                            min={minForItem}
                            max={lineTotal}
                            step="0.01"
                            disabled={requiresDelivery}
                            data-testid={`basket-item-online-${item.listingId}`}
                            value={online.toFixed(2)}
                            onChange={e => handleOnlineAmountChange(item, e.target.value)}
                            className="w-full py-1.5 pr-2 text-sm text-right outline-none disabled:bg-gray-50 disabled:text-gray-400"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] font-semibold text-gray-500 flex items-center gap-1 mb-1">
                          <Banknote className="w-3 h-3" /> Cash on delivery
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <span className="px-2 text-xs text-gray-500">£</span>
                          <input
                            type="number"
                            min={0}
                            max={lineTotal - minForItem}
                            step="0.01"
                            disabled={requiresDelivery}
                            data-testid={`basket-item-cod-${item.listingId}`}
                            value={codForItem.toFixed(2)}
                            onChange={e => handleCodAmountChange(item, e.target.value)}
                            className="w-full py-1.5 pr-2 text-sm text-right outline-none disabled:bg-gray-50 disabled:text-gray-400"
                          />
                        </div>
                      </div>
                    </div>
                    {itemError && (
                      <p data-testid={`basket-item-error-${item.listingId}`} className="text-xs text-red-600 mt-1">{itemError}</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-800 mb-1">How will you get your items?</p>
              <p className="text-xs text-gray-500 mb-3">
                Sellers set their own delivery — TransitDeal can&apos;t quote a fixed price until a carrier does.
              </p>

              <button
                data-testid="basket-delivery-choice-collect"
                onClick={() => setDeliveryChoice('collect')}
                className={`w-full text-left border rounded-xl p-3 mb-2 ${deliveryChoice === 'collect' ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}
              >
                <p className={`text-sm font-semibold ${deliveryChoice === 'collect' ? 'text-green-800' : 'text-gray-800'}`}>
                  I&apos;ll collect it myself
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Pay a deposit now, the rest cash on collection from the seller.
                </p>
              </button>

              <button
                data-testid="basket-delivery-choice-post-job"
                onClick={() => setDeliveryChoice('post-job')}
                className={`w-full text-left border rounded-xl p-3 ${deliveryChoice === 'post-job' ? 'border-green-600 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center gap-2">
                  <Boxes className="w-4 h-4 text-green-700" />
                  <p className={`text-sm font-semibold ${deliveryChoice === 'post-job' ? 'text-green-800' : 'text-gray-800'}`}>
                    I need it delivered
                  </p>
                  <span className="ml-auto text-[10px] font-bold tracking-wide uppercase bg-green-600 text-white px-2 py-0.5 rounded-full">Recommended</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Paid in full online now — no cash on delivery, since you won't meet the seller in
                  person. Each seller then picks how to ship (their own courier, ShipEngine, or a
                  TransitDeal delivery job).
                </p>
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 h-fit sticky top-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Order Summary</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal ({items.length} item{items.length === 1 ? '' : 's'})</span>
                <span>£{total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Delivery</span>
                <span>Not included — see above</span>
              </div>
              <div className="flex justify-between font-semibold text-green-800">
                <span>Pay Online Now</span>
                <span data-testid="basket-total-online">£{deposit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Cash on Delivery</span>
                <span data-testid="basket-total-cod">£{cod.toFixed(2)}</span>
              </div>
            </div>

            {deliveryChoice === 'collect' && (
              <Link
                to={`/post-job?${new URLSearchParams({
                  description: `Delivery of: ${items.map(i => i.listingTitle).join(', ')}`,
                })}`}
                className="mt-4 block border border-dashed border-green-300 bg-green-50 rounded-lg p-3 hover:bg-green-100"
              >
                <p className="text-xs font-bold text-green-900 flex items-center justify-between">
                  Add a delivery job to this order
                  <span className="text-[10px] font-bold tracking-wide uppercase bg-green-600 text-white px-2 py-0.5 rounded-full">1 click</span>
                </p>
                <p className="text-xs text-green-800 mt-1">
                  Skip posting it separately — bundle a delivery job for this basket now and carriers will quote against it as soon as payment clears.
                </p>
              </Link>
            )}

            <button
              data-testid="basket-place-order-button"
              onClick={() => { setError(null); placeOrderMutation.mutate(); }}
              disabled={placeOrderMutation.isPending || hasErrors}
              className="w-full mt-4 py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {placeOrderMutation.isPending ? 'Starting checkout…' : `Place Order · £${deposit.toFixed(2)}`}
            </button>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
